/**
 * GlobalRulesEditor — shared component for editing the Universal +
 * Framework rule banks ("global rules"). Used in two places:
 *
 *   1. Library → Global Rules — platform-wide edits
 *   2. Sub-tenant → Global Rules tab — per-sub-tenant overrides
 *
 * The component is "dumb" in the API sense: it takes the current
 * rules array, callbacks for save / add / delete / reset, plus
 * metadata (source, can-reset). The parent owns API calls and state.
 *
 * UI shape per scope:
 *   • Header banner showing source: "in-code default" | "platform" | "sub-tenant override"
 *     and the current version number when applicable.
 *   • Table of rules with columns: # / ID / Severity / Text.
 *   • Each row is editable inline (id text + severity select + text textarea).
 *   • Add row / Delete row buttons.
 *   • Save / Reset to default / Discard changes buttons in the footer.
 */

import React, { useEffect, useMemo, useState } from "react";
import { SeverityBadge } from "./prompt-builder.jsx";

const SEVERITY_OPTIONS = ["critical", "standard", "advisory"];

const SCOPE_META = {
  "universal": {
    label: "Universal",
    accent: "#1565c0",
    desc: "Apply to every agent on every turn — the highest-priority rules in the prompt. Edit carefully: a bad universal rule affects every call across every tenant.",
  },
  "framework-inbound": {
    label: "Framework — Inbound",
    accent: "#7b1fa2",
    desc: "Applied to every inbound task agent. Cover navigation, intent routing, exit sequence, triage preemption.",
  },
  "framework-outbound": {
    label: "Framework — Outbound",
    accent: "#a5824a",
    desc: "Applied to every outbound task agent. Cover HIPAA pre-verification, mid-call openers, outbound exit contract.",
  },
};

export default function GlobalRulesEditor({
  scope,
  initialRules,
  source,                  // "in_code" | "platform" | "sub_tenant"
  version,                 // null when no DB row yet
  onSave,                  // (rules, commitMsg) → Promise
  onResetToDefault,        // optional — present when the level supports it
  resetButtonLabel,        // "Reset to defaults" / "Reset to platform default"
  context,                 // "library" | "sub_tenant" — drives source-banner copy
}) {
  const [rules, setRules] = useState(initialRules || []);
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState(null);
  const [commitMsg, setCommitMsg] = useState("");

  // Re-seed local state when a refresh delivers new initial rules from the parent
  // (e.g. after Save → reload or after switching scope tabs).
  useEffect(() => {
    setRules(initialRules || []);
    setBanner(null);
    setCommitMsg("");
  }, [scope, initialRules]);

  const meta = SCOPE_META[scope] || { label: scope, accent: "#555", desc: "" };

  const dirty = useMemo(() => {
    if (rules.length !== (initialRules || []).length) return true;
    for (let i = 0; i < rules.length; i++) {
      const a = rules[i];
      const b = (initialRules || [])[i];
      if (!b) return true;
      if (a.id !== b.id || a.severity !== b.severity || a.text !== b.text) return true;
    }
    return false;
  }, [rules, initialRules]);

  function setRule(i, patch) {
    setRules((rs) => rs.map((r, j) => (i === j ? { ...r, ...patch } : r)));
  }
  function addRule() {
    // Generate a new id with a sensible prefix per scope
    const prefix = scope === "universal" ? "U" : scope === "framework-inbound" ? "F" : "FO";
    const existing = new Set(rules.map((r) => r.id));
    let n = rules.length + 1;
    while (existing.has(`${prefix}-${n}`)) n++;
    setRules((rs) => [...rs, { id: `${prefix}-${n}`, severity: "standard", text: "" }]);
  }
  function deleteRule(i) {
    setRules((rs) => rs.filter((_, j) => j !== i));
  }
  function moveRule(i, delta) {
    const j = i + delta;
    if (j < 0 || j >= rules.length) return;
    setRules((rs) => {
      const out = [...rs];
      const [r] = out.splice(i, 1);
      out.splice(j, 0, r);
      return out;
    });
  }

  async function handleSave() {
    // Basic validation — every rule needs id + non-empty text
    for (const r of rules) {
      if (!r.id?.trim()) { setBanner({ kind: "err", text: "Every rule needs an id." }); return; }
      if (!r.text?.trim()) { setBanner({ kind: "err", text: `Rule ${r.id} has empty text.` }); return; }
    }
    const ids = rules.map((r) => r.id);
    if (new Set(ids).size !== ids.length) { setBanner({ kind: "err", text: "Duplicate rule ids — each must be unique." }); return; }

    setBusy(true); setBanner(null);
    try {
      await onSave(rules, commitMsg || `Edit ${scope} rules`);
      setBanner({ kind: "ok", text: `Saved ${rules.length} rules.` });
      setCommitMsg("");
    } catch (e) {
      setBanner({ kind: "err", text: e.message || String(e) });
    } finally {
      setBusy(false);
    }
  }

  async function handleReset() {
    if (!onResetToDefault) return;
    if (!window.confirm(`${resetButtonLabel || "Reset to default"}? Your current edits to this scope will be discarded.`)) return;
    setBusy(true); setBanner(null);
    try {
      await onResetToDefault();
      setBanner({ kind: "ok", text: "Reset complete." });
    } catch (e) {
      setBanner({ kind: "err", text: e.message || String(e) });
    } finally {
      setBusy(false);
    }
  }

  function handleDiscard() {
    setRules(initialRules || []);
    setCommitMsg("");
    setBanner({ kind: "ok", text: "Reverted unsaved changes." });
  }

  const sourceBanner = renderSourceBanner({ source, version, context, accent: meta.accent });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Header / source banner */}
      <div style={{
        padding: "10px 14px",
        background: "#fff",
        border: `1px solid #e5e5e5`,
        borderLeft: `4px solid ${meta.accent}`,
        borderRadius: 6,
      }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: meta.accent }}>{meta.label} rules</div>
        <div className="muted small" style={{ marginTop: 4, lineHeight: 1.5 }}>{meta.desc}</div>
        {sourceBanner}
      </div>

      {banner && (
        <div style={{
          padding: "8px 12px", borderRadius: 4,
          background: banner.kind === "ok" ? "#E8F5E9" : "#FFEBEE",
          color: banner.kind === "ok" ? "#2e7d32" : "#c62828",
          fontSize: 13,
        }}>{banner.text}</div>
      )}

      {/* Table of rules */}
      <div style={{ overflowX: "auto", border: "1px solid #e5e5e5", borderRadius: 6 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#f0f3f7" }}>
              <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#2E4F72", textTransform: "uppercase", letterSpacing: 0.5, width: 50 }}>#</th>
              <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#2E4F72", textTransform: "uppercase", letterSpacing: 0.5, width: 120 }}>ID</th>
              <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#2E4F72", textTransform: "uppercase", letterSpacing: 0.5, width: 130 }}>Severity</th>
              <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#2E4F72", textTransform: "uppercase", letterSpacing: 0.5 }}>Text</th>
              <th style={{ padding: "8px 10px", width: 130 }}></th>
            </tr>
          </thead>
          <tbody>
            {rules.length === 0 ? (
              <tr>
                <td colSpan={5} className="muted small" style={{ padding: 18, textAlign: "center" }}>
                  No rules. Click "+ Add rule" to start.
                </td>
              </tr>
            ) : rules.map((r, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafbfc", borderTop: i === 0 ? "none" : "1px solid #f0f0f0" }}>
                <td style={{ padding: "8px 10px", verticalAlign: "top", fontFamily: "ui-monospace", fontSize: 11, fontWeight: 600 }}>{i + 1}</td>
                <td style={{ padding: "8px 10px", verticalAlign: "top" }}>
                  <input
                    type="text"
                    value={r.id}
                    onChange={(e) => setRule(i, { id: e.target.value })}
                    style={{ width: "100%", padding: 4, fontSize: 11, fontFamily: "ui-monospace", border: "1px solid #ddd", borderRadius: 3 }}
                  />
                </td>
                <td style={{ padding: "8px 10px", verticalAlign: "top" }}>
                  <select
                    value={r.severity}
                    onChange={(e) => setRule(i, { severity: e.target.value })}
                    style={{ width: "100%", padding: 4, fontSize: 11, border: "1px solid #ddd", borderRadius: 3 }}
                  >
                    {SEVERITY_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <div style={{ marginTop: 4 }}><SeverityBadge severity={r.severity} /></div>
                </td>
                <td style={{ padding: "8px 10px", verticalAlign: "top" }}>
                  <textarea
                    value={r.text}
                    onChange={(e) => setRule(i, { text: e.target.value })}
                    rows={Math.max(2, Math.min(10, Math.ceil((r.text || "").length / 90)))}
                    style={{ width: "100%", padding: 6, fontSize: 12, fontFamily: "inherit", lineHeight: 1.5, border: "1px solid #ddd", borderRadius: 3, resize: "vertical" }}
                  />
                </td>
                <td style={{ padding: "8px 10px", verticalAlign: "top", whiteSpace: "nowrap" }}>
                  <button type="button" className="btn btn-sm" onClick={() => moveRule(i, -1)} disabled={i === 0} title="Move up">↑</button>
                  <button type="button" className="btn btn-sm" onClick={() => moveRule(i, 1)} disabled={i === rules.length - 1} title="Move down" style={{ marginLeft: 4 }}>↓</button>
                  <button type="button" className="btn btn-sm" onClick={() => deleteRule(i)} title="Delete" style={{ marginLeft: 4, color: "#c62828" }}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer actions */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <button type="button" className="btn sm" onClick={addRule} disabled={busy}>
          + Add rule
        </button>
        <input
          type="text"
          placeholder="Commit message (optional)"
          value={commitMsg}
          onChange={(e) => setCommitMsg(e.target.value)}
          disabled={busy}
          style={{ flex: 1, minWidth: 200, padding: 6, fontSize: 12, border: "1px solid #ccc", borderRadius: 4 }}
        />
        <button type="button" className="btn sm" onClick={handleDiscard} disabled={busy || !dirty}>
          Discard changes
        </button>
        {onResetToDefault && (
          <button type="button" className="btn sm" onClick={handleReset} disabled={busy}>
            {resetButtonLabel || "Reset to default"}
          </button>
        )}
        <button type="button" className="btn sm primary" onClick={handleSave} disabled={busy || !dirty}>
          {busy ? "Saving…" : `Save (${rules.length} rule${rules.length === 1 ? "" : "s"})`}
        </button>
      </div>
    </div>
  );
}

function renderSourceBanner({ source, version, context, accent }) {
  let label, color;
  if (source === "in_code") {
    label = "Showing in-code defaults — no DB row yet. Saving will create the first version.";
    color = "#888";
  } else if (source === "platform") {
    label = context === "sub_tenant"
      ? `Inheriting from PLATFORM (v${version}). Saving here creates a sub-tenant override.`
      : `Platform live · v${version}`;
    color = "#1565c0";
  } else if (source === "sub_tenant") {
    label = `Sub-tenant override · v${version}. Reset to platform to drop this override.`;
    color = "#ef6c00";
  } else {
    label = source;
    color = "#666";
  }
  return (
    <div style={{ marginTop: 8, fontSize: 12, color }}>
      <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 4, background: accent, marginRight: 6, verticalAlign: "middle" }} />
      {label}
    </div>
  );
}
